import * as React from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight, Circle, ChevronDown, Mail, X, UserPlus } from 'lucide-react'
import { useAuth } from '../../context/authContext';
import { useState } from 'react';



const DropdownMenu = DropdownMenuPrimitive.Root
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
const DropdownMenuGroup = DropdownMenuPrimitive.Group
const DropdownMenuPortal = DropdownMenuPrimitive.Portal
const DropdownMenuSub = DropdownMenuPrimitive.Sub
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

const DropdownMenuSubTrigger = React.forwardRef(
  ({ className, inset, children, ...props }, ref) => (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={(
        'flex cursor-default select-none items-center rounded-sm px-2 py-3 text-base outline-none focus:bg-accent data-[state=open]:bg-accent',
        inset && 'pl-8',
        className
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ml-auto h-4 w-4" />
    </DropdownMenuPrimitive.SubTrigger>
  )
)
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef(
  ({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={(
        'z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  )
)
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName

const DropdownMenuContent = React.forwardRef(
  ({ className, sideOffset = 4, ...props }, ref) => (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={(
          'z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
)
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef(
  ({ className, inset, ...props }, ref) => (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={(
        'relative flex cursor-default select-none items-center rounded-sm px-2 py-3 text-base outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  )
)
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef(
  ({ className, children, checked, ...props }, ref) => (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={(
        'relative flex cursor-default select-none items-center rounded-sm py-3 pl-8 pr-2 text-base outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
)
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={(
        'relative flex cursor-default select-none items-center rounded-sm py-3 pl-8 pr-2 text-base outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Circle className="h-2 w-2 fill-current" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
)
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef(
  ({ className, inset, ...props }, ref) => (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={(
        'px-2 py-1.5 text-sm font-semibold',
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  )
)
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef(
  ({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={('-mx-1 my-1 h-px bg-muted', className)}
      {...props}
    />
  )
)
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({ className, ...props }) => {
  return (
    <span
      className={(
        'ml-auto text-xs tracking-widest opacity-60',
        className
      )}
      {...props}
    />
  )
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

const Dropdown = ({ 
  items = [], 
  label = 'Select an option', 
  placeholder = 'Select...',
  className = '', 
  width = 'w-64', 
  onSelect = () => {},
  selected: controlledSelected,
  onChange,
  showSelectedInTrigger = true,
  disabled = false // Add disabled prop
}) => {
  const [internalSelected, setInternalSelected] = useState(items[0] || '');
  
  // Use controlled pattern if selected prop is provided
  const selected = controlledSelected !== undefined ? controlledSelected : internalSelected;
  
  const handleSelect = (value) => {
    if (disabled) return; // Prevent selection when disabled
    
    if (onChange) {
      onChange(value);
    } else {
      setInternalSelected(value);
    }
    onSelect(value);
  };

  return (
    <div className={`flex flex-col mb-4 ${width} ${className}`}>
      {label && <label className={`mb-1 text-sm font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
        {label}
      </label>}
      <DropdownMenu>
        <DropdownMenuTrigger 
          disabled={disabled}
          className={`w-full border ${disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-white'} rounded px-3 py-2 text-left ${disabled ? 'text-gray-400' : 'text-gray-800'} focus:outline-none focus:ring-2 focus:ring-red-500 flex justify-between items-center`}
        >
          <span className="truncate">
            {showSelectedInTrigger ? (selected || placeholder) : placeholder}
          </span>
          <ChevronDown className={`h-4 w-4 ml-2 ${disabled ? 'opacity-30' : 'opacity-50'}`} />
        </DropdownMenuTrigger>
        {!disabled && ( // Only render content when not disabled
          <DropdownMenuContent 
            className="bg-white shadow-lg border rounded max-h-60 overflow-y-auto w-[var(--radix-dropdown-menu-trigger-width)]"
            style={{ zIndex: 9999 }}
            align="start"
          >
            {items.map((item, index) => (
              <DropdownMenuItem
                key={index}
                onSelect={() => handleSelect(item)}
                className="cursor-pointer px-3 py-3 hover:bg-gray-100 text-base"
              >
                {item}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
    </div>
  );
};

// Small Dropdown
const SmallDropdown = ({ 
  items = [], 
  label = 'Select an option', 
  placeholder = 'Select...',
  className = '', 
  width = 'w-48', 
  onSelect = () => {},
  selected: controlledSelected,
  onChange,
  showSelectedInTrigger = true,
  disabled = false,
  searchable = false // Add searchable prop
}) => {
  const [internalSelected, setInternalSelected] = useState(items[0] || '');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Controlled or internal selection
  const selected = controlledSelected !== undefined ? controlledSelected : internalSelected;
  
  // Filter items based on search query
  const filteredItems = searchable ? items.filter(item => item.toLowerCase().includes(searchQuery.toLowerCase())) : items;
  
  const handleSelect = (value) => {
    if (disabled) return;
    if (onChange) {
      onChange(value);
    } else {
      setInternalSelected(value);
    }
    onSelect(value);
    setSearchQuery('');
  };

  return (
    <div className={`flex flex-col ${className} mb-1 ${width}`}>
      {label && (
        <label className={`mb-0.5 text-[15px] font-medium underline ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
          {label}
        </label>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger 
          disabled={disabled}
          className={`w-full border ${disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-white'} 
                      rounded px-1.5 h-6 text-[11px] text-left 
                      ${disabled ? 'text-gray-400' : 'text-gray-800'} 
                      focus:outline-none focus:ring-1 focus:ring-red-400 
                      flex justify-between items-center`}
        >
          <span className="truncate">
            {showSelectedInTrigger ? (selected || placeholder) : placeholder}
          </span>
          <ChevronDown className={`h-3 w-3 ml-1 ${disabled ? 'opacity-30' : 'opacity-50'}`} />
        </DropdownMenuTrigger>

        {!disabled && (
          <DropdownMenuContent 
            className="bg-white shadow-lg border rounded max-h-96 overflow-y-auto w-[var(--radix-dropdown-menu-trigger-width)]"
            style={{ zIndex: 9999, maxHeight:500 }}
            align="start"
          >
            {/* Add search input when searchable is true */}
            {searchable && (
              <div className="sticky top-0 bg-white p-2 border-b">
                <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDownCapture={(e) => e.stopPropagation()} className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-red-400" />
              </div>
            )}
            
            {/* Render filtered items */}
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <DropdownMenuItem key={index} onSelect={() => handleSelect(item)} className="cursor-pointer px-3 py-3 hover:bg-gray-100 text-base" > {item} </DropdownMenuItem>
              ))
            ) : (
              <div className="px-3 py-3 text-gray-500 text-sm">No results found</div>
            )}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
    </div>
  );
};
//=============================================

const DropDownMenu = ({ open, title, items }) => {
  const { logout } = useAuth();
  return (
    <div className="relative">
      <div className="flex items-center space-x-1 cursor-pointer select-none">
        <strong className=" w-[100px]">{title}</strong>
        <ChevronDown size={16} className="text-gray-500" />
      </div>

      {open && (
        <div className="absolute top-10 right-0 mt-2 w-48 bg-white border rounded shadow z-50">
          <ul className="text-base text-gray-700">
            {items.map(({ label, href, color }, idx) => (
              <li key={idx}>
                {label === 'Logout' ? (
                  <button
                    onClick={() => logout()}
                    className={`block w-full text-left px-4 py-3 hover:bg-gray-100 cursor-pointer ${color === 'red' ? 'text-red-500' : ''
                      }`}
                  >
                    {label}
                  </button>
                ) : (
                  <a
                    href={href}
                    className={`block px-4 py-3 hover:bg-gray-100 cursor-pointer ${color === 'red' ? 'text-red-500' : ''
                      }`}
                  >
                    {label}
                  </a>
                )}
              </li>
            ))}

          </ul>
        </div>
      )}
    </div>
  );
};

// New SmallDropDownMenu component
const SmallDropDownMenu = ({ open, title, items, onLogout }) => {
  return (
    <div className="relative">
      <div className="flex items-center space-x-1 cursor-pointer select-none">
        <strong className="text-xs w-[80px] truncate">{title}</strong>
        <ChevronDown size={12} className="text-gray-500" />
      </div>

      {open && (
        <div className="absolute top-8 right-0 mt-1 w-40 bg-white border rounded shadow z-50">
          <ul className="text-base text-gray-700">
            {items.map(({ label, href, color }, idx) => (
              <li key={idx}>
                {label === 'Logout' ? (
                  <button
                    onClick={onLogout}
                    className={`block w-full text-left px-3 py-3 hover:bg-gray-100 cursor-pointer ${
                      color === 'red' ? 'text-red-500' : ''
                    }`}
                  >
                    {label}
                  </button>
                ) : (
                  <a
                    href={href}
                    className={`block px-3 py-3 hover:bg-gray-100 cursor-pointer ${
                      color === 'red' ? 'text-red-500' : ''
                    }`}
                  >
                    {label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Additional Emails Dropdown Component
const AdditionalEmailsDropdown = ({ emails, selectedEmails, onEmailsChange, label = "Customer Location - Additional Emails", disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter emails based on search term
  const filteredEmails = emails.filter(email => 
    email.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedEmails.includes(email)
  );

  // Handle email selection
  const handleEmailSelect = (email) => {
    if (disabled) return;
    const newSelectedEmails = [...selectedEmails, email];
    onEmailsChange(newSelectedEmails);
    setSearchTerm('');
  };

  // Handle email removal
  const handleEmailRemove = (emailToRemove) => {
    if (disabled) return;
    const newSelectedEmails = selectedEmails.filter(email => email !== emailToRemove);
    onEmailsChange(newSelectedEmails);
  };

  // Handle dropdown toggle
  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <label className={`block text-sm font-medium mb-1 underline ${disabled ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '15px' }}>
        {label}
      </label>
      
      {/* Selected emails display */}
      <div className={`h-7 px-1.5 border rounded-md cursor-pointer flex items-center ${disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-white'}`} onClick={toggleDropdown}>
        {selectedEmails.length === 0 ? (
          <div className="flex items-center w-full">
            <Mail className={`h-3 w-3 mr-2 ${disabled ? 'text-gray-400' : 'text-gray-500'}`} />
            <span className={`text-[11px] ${disabled ? 'text-gray-400' : 'text-gray-500'}`}>
              {disabled ? 'Please select a customer first' : 'Select additional emails...'}
            </span>
            <ChevronDown className={`h-3 w-3 ml-auto ${disabled ? 'opacity-30' : 'opacity-50'}`} />
          </div>
        ) : (
          <div className="flex flex-wrap gap-1 items-center w-full overflow-hidden">
            {selectedEmails.map((email, index) => (
              <div
                key={index}
                className={`flex items-center px-2 py-0.5 rounded text-xs flex-shrink-0 ${disabled ? 'bg-gray-100 text-gray-500' : 'bg-red-100 text-red-700'}`}
                onClick={(e) => { e.stopPropagation(); handleEmailRemove(email); }}
              >
                <span>{email}</span>
                <X className={`h-2 w-2 ml-1 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:text-red-900'}`} />
              </div>
            ))}
            <ChevronDown className={`h-3 w-3 ml-auto text-gray-500 flex-shrink-0 ${disabled ? 'opacity-30' : 'opacity-50'}`} />
          </div>
        )}
      </div>

      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <input type="text" placeholder="Search emails..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500" onClick={(e) => e.stopPropagation()} />
          </div>

          {/* Email list */}
          <div className="max-h-48 overflow-y-auto">
            {filteredEmails.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 text-center">
                {searchTerm ? 'No emails found' : 'No additional emails available'}
              </div>
            ) : (
              filteredEmails.map((email, index) => (
                <div key={index} className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center" onClick={() => handleEmailSelect(email)} >
                  <Mail className="h-4 w-4 mr-2 text-gray-500" />
                  <span>{email}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && !disabled && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// Contact Dropdown — Primary Contact selector with optional "Add new contact" entry.
// Items are objects: { key, name, subtitle }. Selected is matched on `key`.
const ContactDropdown = ({
  label = 'Primary Contact',
  items = [],
  selectedKey,
  onSelect = () => {},
  onAddNew,
  disabled = false,
  width = 'w-44',
  placeholder = 'Select contact...'
}) => {
  const selected = items.find((i) => i.key === selectedKey);

  return (
    <div className={`flex flex-col mb-1 ${width}`}>
      {label && (
        <label className={`mb-0.5 text-[15px] font-medium underline ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
          {label}
        </label>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={disabled}
          className={`w-full border ${disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-white'}
                      rounded px-1.5 h-6 text-[11px] text-left
                      ${disabled ? 'text-gray-400' : 'text-gray-800'}
                      focus:outline-none focus:ring-1 focus:ring-red-400
                      flex justify-between items-center`}
        >
          <span className="truncate">{selected?.name || placeholder}</span>
          <ChevronDown className={`h-3 w-3 ml-1 ${disabled ? 'opacity-30' : 'opacity-50'}`} />
        </DropdownMenuTrigger>

        {!disabled && (
          <DropdownMenuContent
            className="bg-white shadow-lg border rounded max-h-96 overflow-y-auto w-[var(--radix-dropdown-menu-trigger-width)]"
            style={{ zIndex: 9999, maxHeight: 500, minWidth: 220 }}
            align="start"
          >
            {items.length === 0 ? (
              <div className="px-3 py-3 text-gray-500 text-sm">No contacts available</div>
            ) : (
              items.map((item) => (
                <DropdownMenuItem
                  key={item.key}
                  onSelect={() => onSelect(item)}
                  className={`cursor-pointer px-3 py-2 hover:bg-gray-100 text-sm ${item.key === selectedKey ? 'bg-red-50' : ''}`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-800">{item.name || '—'}</span>
                    {item.subtitle && (
                      <span className="text-[11px] text-gray-500 truncate">{item.subtitle}</span>
                    )}
                  </div>
                </DropdownMenuItem>
              ))
            )}
            {onAddNew && (
              <>
                <div className="border-t my-1" />
                <DropdownMenuItem
                  onSelect={() => onAddNew()}
                  className="cursor-pointer px-3 py-2 hover:bg-red-50 text-sm text-red-600 font-medium flex items-center"
                >
                  <UserPlus className="h-3 w-3 mr-2" />
                  Add New Contact
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
    </div>
  );
};

export {
  DropdownMenu,
  SmallDropdown,
  SmallDropDownMenu,
  AdditionalEmailsDropdown,
  ContactDropdown,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
  Dropdown,
  DropDownMenu,
}
